/* eslint import/no-webpack-loader-syntax: off */
import React, { FC, useEffect, useRef, useMemo, ReactNode } from 'react'
import * as Styles from './modal.styles'
import { ChildrenProp } from '../../forms/hocs/types'
import { IconClose } from '@at/design-system-icons'

export enum ModalSize {
    FULL_SCREEN,
    LARGE,
    MEDIUM,
    SMALL,
}

interface ModalProps extends ChildrenProp {
    className?: string
    closeModalFn?: () => void
    fullScreen?: boolean
    modalHashName?: string
    modalSize?: ModalSize
    isModalShowing: boolean
    footerChildren: ReactNode
}

export const Modal: FC<ModalProps> = ({
    children,
    footerChildren,
    closeModalFn,
    className,
    modalSize = ModalSize.MEDIUM,
    modalHashName = 'modal-content',
    isModalShowing,
}) => {
    const modalRef = useRef<HTMLDivElement>(null)

    const onClickModalOverlay = closeModalFn
        ? (event: React.MouseEvent<HTMLDivElement>) => {
              if (event.target === modalRef.current) {
                  closeModalFn()
              }
          }
        : undefined

    const onKeyDownHandler = useMemo(
        () =>
            closeModalFn
                ? (event: KeyboardEvent | React.KeyboardEvent<HTMLButtonElement>) => {
                      if (event.key === 'Esc' || event.key === 'Escape' || event.keyCode === 27) {
                          closeModalFn()
                      }
                  }
                : undefined,
        [closeModalFn]
    )

    useEffect(() => {
        if (!onKeyDownHandler || !closeModalFn) return

        document.addEventListener('keydown', onKeyDownHandler)
        window.addEventListener('popstate', closeModalFn)

        return () => {
            document.removeEventListener('keydown', onKeyDownHandler)
            window.addEventListener('popstate', closeModalFn)
        }
    }, [onKeyDownHandler, closeModalFn])

    return (
        <Styles.ModalOverlay ref={modalRef} id={modalHashName} onClick={onClickModalOverlay} role="dialog">
            <Styles.ModalDialog modalSize={modalSize}>
                <Styles.Modal className={className} role="document" isModalShowing={isModalShowing}>
                    <Styles.ModalHeader>
                        <Styles.ModalTitle as="h1">Filter and sort</Styles.ModalTitle>
                        <Styles.ModalButton
                            disabled={!closeModalFn && !onKeyDownHandler}
                            onClick={closeModalFn}
                            onKeyDown={onKeyDownHandler}
                            autoFocus
                            aria-label="Close"
                        >
                            <IconClose title="Close" />
                        </Styles.ModalButton>
                    </Styles.ModalHeader>
                    <Styles.ModalBody id={`${modalHashName}-body`}>
                        <Styles.ModalContent>{children}</Styles.ModalContent>
                    </Styles.ModalBody>
                    <Styles.ModalFooter>{footerChildren}</Styles.ModalFooter>
                </Styles.Modal>
            </Styles.ModalDialog>
        </Styles.ModalOverlay>
    )
}
